import { Image, Link, StyleSheet, Text, View } from '@react-pdf/renderer'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import type { PdfProps } from 'features/reporting/types'
import type { SectionAddendum } from 'pages/reporting/reports/rug/rugReportData'
import { getAddendumPages } from 'pages/reporting/reports/rug/utils/addendums/addendumContent'
import { addendumAttachmentAnchorUri } from 'pages/reporting/reports/rug/utils/addendums/attachmentAnchors'
import React from 'react'

const styles = StyleSheet.create({
  page: { height: 612, minHeight: 612 },
  subtitle: { fontSize: 10, fontWeight: 'bold', marginBottom: 8 },
  content: { width: '100%', flexGrow: 1, flexShrink: 1, minHeight: 0 },
  image: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  anchor: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  fill: { width: '100%', height: '100%' },
  caption: { fontSize: 9, marginTop: 8 },
})

interface Props {
  sectionId: string
  sectionTitle: string
  addendums: SectionAddendum[]
  jobNumber: string
  revisionNumber: number
  imageHeader: React.ReactNode
  imageSources: ReadonlyMap<string, string> | undefined
  onRender?: PdfProps['onRender']
}

export function AddendumContentPages({
  sectionId,
  sectionTitle,
  addendums,
  jobNumber,
  revisionNumber,
  imageHeader,
  imageSources,
  onRender,
}: Props) {
  let pageNumber = 0
  return (
    <>
      {addendums.flatMap((addendum, addendumIndex) =>
        getAddendumPages(addendum).map(
          ({ attachment, sourcePageIndex }, pageIndex) => {
            const track = pageNumber++ === 0 && onRender
            const imageSource =
              attachment?.kind === 'image'
                ? imageSources?.get(attachment.id)
                : undefined
            if (attachment?.kind === 'image' && !imageSource)
              throw new Error(`Image is not prepared: ${attachment.fileName}`)
            return (
              <ReportPage
                key={`${addendum.id ?? addendumIndex}-${attachment?.id ?? 'blank'}-${pageIndex}`}
                size='LETTER'
                orientation='landscape'
                wrap={false}
                fillPage
                pageStyle={styles.page}
                tracker={track ? { sectionId, onRender } : undefined}
                header={<PageHeader title={sectionTitle} />}
                footer={
                  <PageFooter
                    jobNumber={jobNumber}
                    revisionNumber={revisionNumber}
                  />
                }>
                {imageHeader}
                {addendum.pageSubtitle && (
                  <Text style={styles.subtitle}>{addendum.pageSubtitle}</Text>
                )}
                {attachment && (
                  <View style={styles.content}>
                    {attachment.kind === 'image' && (
                      <Image src={imageSource} style={styles.image} />
                    )}
                    {attachment.kind === 'pdf' && (
                      <Link
                        src={addendumAttachmentAnchorUri(
                          attachment.id,
                          sourcePageIndex ?? 0
                        )}
                        style={styles.anchor}>
                        <View style={styles.fill} />
                      </Link>
                    )}
                  </View>
                )}
                {attachment?.caption && (
                  <Text style={styles.caption}>{attachment.caption}</Text>
                )}
              </ReportPage>
            )
          }
        )
      )}
    </>
  )
}
